import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';

export interface InviteField {
  readonly name: string;
  readonly label: string;
  readonly type?: 'text' | 'email';
  readonly hint?: string;
}

interface Props {
  readonly open: boolean;
  readonly title: string;
  /** Shown above the fields. This is where the consequence of sending goes. */
  readonly preamble: string;
  readonly fields: readonly InviteField[];
  readonly submitLabel: string;
  readonly onSubmit: (values: Record<string, string>) => Promise<void>;
  readonly onClose: () => void;
}

/**
 * One dialog for both invitations, because they ask nearly the same thing and
 * two near-identical forms would drift.
 *
 * A native <dialog>, as `AboutDialog` — focus trapping, Escape-to-close and
 * inertness of the page behind it come from the platform, which is a lot of
 * accessibility to reimplement badly.
 *
 * The error is rendered from the API's own message rather than a paraphrase.
 * The refusals here are meaningful — an unverified account, a cohort you do not
 * run — and the server is the only thing that knows which one applies.
 */
export function InviteDialog({
  open,
  title,
  preamble,
  fields,
  submitLabel,
  onSubmit,
  onClose,
}: Props): JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      // Fresh every time. A half-typed address left over from a cancelled
      // invitation is exactly the sort of thing that gets sent to the wrong
      // person.
      setValues({});
      setError(null);
      setSending(false);
      dialog.showModal();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    setSending(true);
    setError(null);

    onSubmit(values)
      .then(() => {
        onClose();
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not send the invitation.');
        setSending(false);
      });
  };

  return (
    <dialog ref={ref} className="invite" onClose={onClose} onCancel={onClose}>
      <h2 className="invite__title">{title}</h2>
      <p className="invite__preamble">{preamble}</p>

      <form onSubmit={submit}>
        {fields.map((field) => (
          <label key={field.name} className="invite__field">
            <span className="invite__label">{field.label}</span>
            <input
              className="invite__input"
              type={field.type ?? 'text'}
              required
              value={values[field.name] ?? ''}
              onChange={(event) =>
                setValues((current) => ({ ...current, [field.name]: event.target.value }))
              }
            />
            {field.hint !== undefined && <span className="invite__hint">{field.hint}</span>}
          </label>
        ))}

        {/* role="alert" so a screen reader hears the refusal rather than
            leaving somebody pressing a button that silently does nothing. */}
        {error !== null && (
          <p className="invite__error" role="alert">
            {error}
          </p>
        )}

        <div className="invite__actions">
          <button type="button" className="ghost" onClick={onClose} disabled={sending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={sending}>
            {sending ? 'Sending…' : submitLabel}
          </button>
        </div>
      </form>
    </dialog>
  );
}
